import { useState, useRef, useLayoutEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * CollapsibleText — 긴 설명·근거 텍스트를 기본 N줄로 접고 "더보기/접기"로 펼친다.
 *
 * AI 추천 결과 화면(요약·시나리오 근거·최종 근거)의 텍스트 밀도를 낮춰 가시성을 개선하기 위한
 * 순수 표시 컴포넌트. 색·폰트는 props(sx, toggleColor)로 주입한다.
 *
 * (주의) 토글은 실제로 클램프되어 잘릴 때만 노출한다(scrollHeight > clientHeight 측정).
 *    글자 수 추정이 아니라 DOM 측정이라, 짧아서 안 잘리는 텍스트엔 "더보기"가 붙지 않는다.
 */
export default function CollapsibleText({
  text,
  lines = 2,
  sx = {},
  toggleColor = "#2563eb",
  moreLabel,
  lessLabel,
}) {
  const { t } = useTranslation();
  const more = moreLabel ?? t("moreLabel");
  const less = lessLabel ?? t("lessLabel");
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // 클램프된 상태에서만 측정한다. 펼친 상태(open)에서는 클램프가 없어 측정값이 0이 되므로 건너뛴다.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, lines, open]);

  if (text == null || text === "") return null;
  const clampSx = open
    ? {}
    : { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" };

  return (
    <Box>
      <Typography ref={ref} sx={{ wordBreak: "break-word", ...sx, ...clampSx }}>{String(text)}</Typography>
      {overflowing && (
        <Typography
          component="span"
          onClick={() => setOpen(o => !o)}
          sx={{
            display: "inline-block", mt: "2px", fontSize: 9, fontWeight: 700,
            cursor: "pointer", color: toggleColor, userSelect: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {open ? `${less} ▲` : `${more} ▾`}
        </Typography>
      )}
    </Box>
  );
}
